/**
 * O que os cartões do atendimento fazem quando o consultor clica — spec-010 do
 * `project-plataforma-por-chat`, [T017]; extraído dos dois embrulhos na
 * iteração 2 da review de front (achado I-4).
 *
 * ## Por que a ação mora aqui, e não no `.vue`
 *
 * Decisão D8 do plan: "abrir a tela" é empurrar uma ROTA, e isso se resolve no
 * browser, agora — mandá-la pelo canal `respond` obrigaria quem está acima do
 * `chat-message` a conhecer as rotas de cada módulo. Os cartões EMITEM; quem
 * age é o embrulho (`ia/cartoes/*.js`), por estas funções. É o que mantém os
 * `.vue` montáveis num teste sem router.
 *
 * ## Por que os dois embrulhos passaram a compartilhar uma função
 *
 * `recentes.js` e `mensagens-do-cliente.js` tinham cada um a sua versão de
 * "empurra a rota do atendimento", e as duas engoliam a falha: `empurrarRota`
 * absorve só o `NavigationDuplicated` e RELANÇA o resto (guarda de rota,
 * `NavigationCancelled`, `NavigationRedirected`). Sem `.catch`, uma dessas
 * falhas vira promise rejeitada solta no console e um clique que não faz nada,
 * sem mensagem. A receita inteira (router? → `empurrarRota` → aviso de aba
 * barrada → snack com a saída) mora em `ui-registry/acoes-compartilhadas`,
 * compartilhada pelos seis parciais (review #2041) — aqui só entram a rota e a
 * mensagem do atendimento.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { abrirRotaDoCartao } from '@modules/aawz-intelligence-v2/ui-registry/acoes-compartilhadas'
import { ROTA_DO_ATENDIMENTO } from '@modules/atendimento/ia/chaves'
import { QUERY_CONVERSA } from '@modules/atendimento/constants/Text'

export { ROTA_DO_ATENDIMENTO }

// A mensagem traz a SAÍDA junto: erro sem próxima ação deixa o consultor
// travado na conversa sem saber por onde chegar à caixa.
const ERRO_AO_NAVEGAR =
  'Não foi possível abrir o atendimento. Tente pelo menu do CRM.'

/**
 * Empurra a rota da caixa de atendimento, com a query que vier.
 *
 * @param {Object} vm A instância do embrulho (de onde saem `$router` e
 *                    `$showSnack`).
 * @param {Object} [query] Query da rota (o deep link da conversa).
 * @returns {Promise|null} `null` quando a superfície não tem router — o chat
 *                    também roda embutido em telas sem navegação, e um
 *                    `undefined.push` no clique derrubaria o render do thread.
 */
export function abrirTelaDeAtendimento(vm, query) {
  const rota = query
    ? { name: ROTA_DO_ATENDIMENTO, query: query }
    : { name: ROTA_DO_ATENDIMENTO }
  return abrirRotaDoCartao(vm, rota, { mensagemDeErro: ERRO_AO_NAVEGAR })
}

/**
 * Abre a tela JÁ com a conversa: o id vai na query (`?conversa=<id>`), que a
 * view lê no `initializeModule()` (`utils/conversa-da-rota`).
 *
 * ★ Nada é despachado no store daqui. A tela abre em NOVA aba
 * (`ABRIR_TELA_EM_NOVA_ABA`), cujo Vuex nasce vazio — `selectConversa` na aba
 * de origem esvaziava as mensagens do popup/FAB que continuava aberto ali e
 * não chegava à aba nova.
 */
export function abrirConversaDeAtendimento(vm, conversa) {
  const id = conversa && conversa.id
  const query = id ? { [QUERY_CONVERSA]: String(id) } : null
  return abrirTelaDeAtendimento(vm, query)
}
